import { MainNavbarInterface } from '@type/header-types';
import { LogoBjLink } from '@components/layout/LogoBj/LogoBjLink';
import { ErrorBoundary } from '@components/common';
import {
  useCallback, useEffect, useState,
} from 'react';
import { clsx } from 'clsx';
import { useScrollDirection } from '@hooks/common/useScrollDirection';
import { SearchSection } from '@components/layout/Header/MainNavbar/search/SearchSection';
import { SearchJobSection } from '@components/layout/Header/MainNavbar/search/SearchJobSection';
import { LanguageList } from '@components/layout/LanguageSwitch/LanguageList';
import { MobileMenu } from '@components/layout/Header/MainNavbar/mobileMenu/MobileMenu';
import { isHireRoute } from '@lib/functions/routes/isHireRoute';
import { isCompanyJobRoute } from '@lib/functions/routes/isCompanyJobRoute';
import { isApplicantsFlowRoute } from '@lib/functions/routes/isApplicantsFlowRoute';
import { isMcRoute } from '@lib/functions/routes/isMcRoute';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { getNavbarTheme } from './config';
import { LoginButton, MainMenuLinks } from './links';
import { MessagesSection } from './messages/MessagesSection';
import { ProfileSection } from './profile/ProfileSection';
import { MainNavbarPlaceholder } from './placeholders/MainNavbarPlaceholder';
import { SearchMyJobSection } from './search/SearchMyJobSection';
import { FilterButton } from './search/FilterButton';
import { MapButton } from './search/MapButton';

/**
 * @description Main navbar component.
 * * **headerInfo** - authenticated user info
 * * **navbarTheme** - the navbar theme (transparent, default, etc.)
 * * **currentRoute** - the current route. Used to highlight the active link in the navbar
 * * **notPinned** - if the navbar is not pinned to the top of the page. When we scroll down,
 * the navbar will be pinned to the top of the page, so we can apply a different style to it.
 * * **locale** - the current locale, with default value 'ro'
 */
export const MainNavbar = (props: MainNavbarInterface) => {
  // Main component props with default values
  const {
    headerInfo = undefined,
    headerInfoLoading,
    headerInfoValidating,
    navbarTheme = 'default',
    currentRoute = '',
    notPinned = true,
    locale = 'ro',
  } = props;

  // Hide bottom nav on mobile while scrolling down
  // *****************************************************
  const isBottomNavHidden = useScrollDirection();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  useEffect(() => {
    // Sync initial state with current screen width (desktop starts open)
    setIsSidebarOpen(window.innerWidth >= 1024);

    const handleStateChange = (event: Event) => {
      const { isOpen } = (event as CustomEvent<{ isOpen: boolean }>).detail;
      setIsSidebarOpen(isOpen);
    };
    window.addEventListener('sidebar-state-change', handleStateChange);
    return () => {
      window.removeEventListener('sidebar-state-change', handleStateChange);
    };
  }, []);

  // We make sure that we load the placeholder first.
  // *****************************************************
  const [initialStatus, setInitialStatus] = useState(true);
  const [twoColumnHeaderOffset, setTwoColumnHeaderOffset] = useState('');
  useEffect(() => {
    setInitialStatus(false);
  }, []);

  useEffect(() => {
    const syncHeaderOffset = () => {
      setTwoColumnHeaderOffset(document.documentElement.style.getPropertyValue('--two-column-header-offset'));
    };

    const handleHeaderOffsetChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ offset?: string }>;
      setTwoColumnHeaderOffset(customEvent.detail?.offset ?? '');
    };

    syncHeaderOffset();
    window.addEventListener('two-column-header-offset-change', handleHeaderOffsetChange);

    return () => {
      window.removeEventListener('two-column-header-offset-change', handleHeaderOffsetChange);
    };
  }, []);


  // Conditionally show/hide components
  // *****************************************************
  const show = {
    placeholder: initialStatus || headerInfoLoading,
    revalidation: !initialStatus && !headerInfoLoading && headerInfoValidating,
    mainMenuLinks: !initialStatus && !headerInfoLoading,
    authUser: !initialStatus && !headerInfoLoading && headerInfo,
  };

  const classes = {
    container: 'py-2 lg:py-0',
    logoContainer: 'w-full',
    logo: 'mr-3 lg:mr-6',
  };

  // Routes that render the global sidebar (MC + /profile + /stories) get the inline
  // candidate search form (or no search context at all), so suppress the header search
  // input and map button to avoid duplicate controls.
  const isMc = isMcRoute(currentRoute);
  const isSidebarStandalone = /^\/(?:[a-z]{2}\/)?(?:profile|stories)(\/.*)?$/.test(currentRoute ?? '');
  // Wallet + salaries pages have no job-search context, so suppress the header search
  // input and map button there too.
  const isWalletOrSalaries = /^\/(?:[a-z]{2}\/)?(?:wallet|salaries)(\/.*)?$/.test(currentRoute ?? '');
  const hideHeaderSearch = isMc || isSidebarStandalone || isWalletOrSalaries;
  const showMapButton = !hideHeaderSearch
    && !isHireRoute(currentRoute)
    && !isApplicantsFlowRoute(currentRoute)
    && !isCompanyJobRoute(currentRoute);

  const renderSearchInput = useCallback(() => {
    if (hideHeaderSearch) return null;
    switch (true) {
      case isApplicantsFlowRoute(currentRoute):
        return null;
      case isCompanyJobRoute(currentRoute):
        return <SearchMyJobSection />;
      case isHireRoute(currentRoute):
        return <SearchSection locale={locale} />;
      case !isHireRoute(currentRoute):
        return <SearchJobSection locale={locale} />;
      default:
        return null;
    }
  }, [currentRoute, locale, hideHeaderSearch]);

  // Render the navbar
  // *****************************************************
  return (
    <>
      <div className={getNavbarTheme(navbarTheme, notPinned)}>
        <div
          className={`container relative flex items-center justify-end lg:justify-normal ${classes.container}`}
          style={twoColumnHeaderOffset ? {
            marginLeft: twoColumnHeaderOffset,
            width: `calc(100% - ${twoColumnHeaderOffset})`,
            maxWidth: `calc(100% - ${twoColumnHeaderOffset})`,
          } : undefined}
        >

          <div
            className={`relative flex items-center justify-start lg:w-fit lg:justify-between ${classes.logoContainer}`}
          >
            {/* Sidebar hamburger button - mobile only, sidebar routes only, hidden when sidebar is open */}
            {(isMc || isSidebarStandalone) && !isSidebarOpen && (
              <button
                type="button"
                data-sidebar-toggle
                className="mr-2 flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-surface-200 focus:outline-none lg:hidden"
                aria-label="Toggle sidebar"
                onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
              >
                <Bars3Icon className="size-6 text-ink" />
              </button>
            )}

            <LogoBjLink
              headerInfo={headerInfo}
              locale={locale}
              className={`-mt-1 ${classes.logo}`}
            />

            {renderSearchInput()}

            {showMapButton && (
              <div className="ml-3 flex items-center">
                <MapButton locale={locale} />
              </div>
            )}

            {/* Filter button - only show on job list page */}
            <div className="ml-3 flex items-center gap-2">
              <FilterButton />
            </div>
          </div>

          {/* Placeholder */}
          {show.placeholder && <MainNavbarPlaceholder />}

          {/* Validating new data (SWR has cache) */}
          {show.revalidation && <div className="absolute inset-0 z-10 flex animate-pulse bg-surface/50" />}

          {// Main menu links (both auth and not auth) and the login button for not auth users
            show.mainMenuLinks && (
              <nav className={clsx(
                'hidden',
                'lg:relative lg:z-auto lg:ml-auto lg:flex lg:h-auto lg:items-center lg:justify-end lg:border-0 lg:bg-transparent',
                'transition-transform duration-300 ease-in-out',
                isBottomNavHidden && 'translate-y-full lg:translate-y-0',
              )}
              >

                {/* main menu links */}
                <MainMenuLinks
                  role={headerInfo?.role}
                  currentRoute={currentRoute}
                  locale={locale}
                />

                {!show.authUser && (
                  <>
                    <div className="relative ml-5 mt-1 hidden shrink-0 lg:block lg:px-1">
                      <LanguageList />
                    </div>

                    {/* Desktop Login button */}
                    <LoginButton className="hidden lg:flex" locale={locale} />
                  </>
                )}

                { // User is authenticated
                  show.authUser && (
                    <>
                      {/* Messages */}
                      <ErrorBoundary fallback={<div className="ml-4 mr-2 size-4.5 rounded-full bg-error lg:mr-4" />}>
                        <MessagesSection
                          className="relative ml-5 hidden lg:block"
                          locale={locale}
                          unseenMessages={headerInfo?.nrUnseenMessages}
                        />
                      </ErrorBoundary>

                      {/* Profile */}
                      <ProfileSection
                        headerInfo={headerInfo}
                        locale={locale}
                        className="ml-5 hidden shrink-0 lg:relative lg:ml-7 lg:block"
                      />
                    </>
                  )
                }

                {/* Mobile menu */}
                <MobileMenu
                  className="flex shrink-0 items-center justify-center lg:hidden"
                  headerInfo={headerInfo}
                  currentRoute={currentRoute}
                  locale={locale}
                />
              </nav>
            )
          }
        </div>
      </div>

      {/* This is used to calculate the height of the opened mobile menu */}
      <div id="menuOffsetSentinel" className="pointer-events-none absolute top-full h-0 w-full" />
    </>
  );
};
